import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslate } from "@refinedev/core";
import { AuthLeftPanel } from "./AuthLayout";
import imgSlide01 from "@/assets/images/signup/img-signup-slide01.png";
import imgSlide02 from "@/assets/images/signup/img-signup-slide02.png";
import imgSlide03 from "@/assets/images/signup/img-signup-slide03.png";
import { radius } from "@libertify/ui";

const SLIDE_IMAGES = [imgSlide01, imgSlide02, imgSlide03];

const SLIDE_CAPTION_KEYS = [
  "pages.auth.register.slideCaption1",
  "pages.auth.register.slideCaption2",
  "pages.auth.register.slideCaption3",
];

function getSlidePos(
  i: number,
  current: number
): "center" | "side-left" | "side-right" {
  if (i === current) return "center";
  if (i === (current - 1 + SLIDE_IMAGES.length) % SLIDE_IMAGES.length)
    return "side-left";
  return "side-right";
}

export const CoverflowCarousel: React.FC = () => {
  const t = useTranslate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setCurrentSlide((s) => (s + 1) % SLIDE_IMAGES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isPaused]);

  return (
    <AuthLeftPanel
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <Tagline>{t("pages.auth.register.tagline")}</Tagline>
      <MainTitle>{t("pages.auth.register.headline")}</MainTitle>
      <CoverflowTrack>
        {SLIDE_IMAGES.map((img, i) => {
          const pos = getSlidePos(i, currentSlide);
          return (
            <SlideItem
              key={i}
              $pos={pos}
              onClick={() => pos !== "center" && setCurrentSlide(i)}
            >
              <SlideImg src={img} alt={`Slide ${i + 1}`} />
            </SlideItem>
          );
        })}
      </CoverflowTrack>
      <SlideCaption>
        {SLIDE_CAPTION_KEYS[currentSlide]
          ? t(SLIDE_CAPTION_KEYS[currentSlide])
          : ""}
      </SlideCaption>
    </AuthLeftPanel>
  );
};

const Tagline = styled.p`
  font-size: 14px;
  color: white;
  opacity: 0.75;
  text-align: center;
  margin: 0 0 clamp(10px, 1.5vh, 16px);
  width: 100%;
  line-height: 1.5;
`;

const MainTitle = styled.h2`
  font-size: clamp(32px, 2vw, 40px);
  font-weight: 600;
  letter-spacing: -0.02em;
  color: white;
  margin: 0 0 clamp(48px, 3vh, 32px);
  line-height: 1.1;
  text-align: center;
  width: clamp(65%, 3vh, 70%);
`;

const CoverflowTrack = styled.div`
  position: relative;
  width: 100%;
  height: clamp(300px, 30vh, 350px);
`;

const SlideItem = styled.div<{ $pos: "center" | "side-left" | "side-right" }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: clamp(250px, 26vh, 320px);
  padding: 8px;
  background-color: #ffffff;
  border-radius: ${radius.box}px;
  transform: ${(p) => {
    if (p.$pos === "center") return "translate(-50%, -50%) scale(1)";
    if (p.$pos === "side-left")
      return "translate(calc(-100% - clamp(48px, 6vh, 88px)), -50%) scale(0.72)";
    return "translate(calc(0% + clamp(48px, 6vh, 88px)), -50%) scale(0.72)";
  }};
  opacity: ${(p) => (p.$pos === "center" ? "1" : "0.30")};
  filter: ${(p) => (p.$pos === "center" ? "none" : "blur(8px)")};
  z-index: ${(p) => (p.$pos === "center" ? 2 : 1)};
  cursor: ${(p) => (p.$pos === "center" ? "default" : "pointer")};
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

const SlideImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: ${radius.box}px;
`;

const SlideCaption = styled.p`
  font-size: 14px;
  color: white;
  opacity: 0.75;
  text-align: center;
  margin: clamp(48px, 2.5vh, 64px) 0 0;
  width: 100%;
  line-height: 1.5;
`;
